import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ViewErrorBoundaryProps {
  children: ReactNode;
}

interface ViewErrorBoundaryState {
  failed: boolean;
}

/**
 * Catches a render/chunk-load failure below the app shell and surfaces it
 * with a reload affordance instead of a white screen. The concrete trigger
 * this guards: a lazily imported view whose `import()` rejects — e.g. a
 * browser holding stale HTML requests a chunk whose hash changed after a
 * deploy — would otherwise throw through Suspense and unmount the whole
 * tree. The rail/top bar stay mounted; only the view area shows the error.
 */
export class ViewErrorBoundary extends Component<ViewErrorBoundaryProps, ViewErrorBoundaryState> {
  override state: ViewErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ViewErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Surfaced (not swallowed): the UI shows the failure; this keeps the
    // underlying cause inspectable in the console for diagnosis.
    console.error('Vaate renderdamine ebaõnnestus', error, info);
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
          <p role="alert" className="text-sm text-muted-foreground">
            Vaate laadimine ebaõnnestus.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground transition-colors hover:bg-accent/40"
          >
            Laadi uuesti
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
